import { useCallback, useMemo } from 'react'

import { ErrorBoundary } from '@appsignal/react'
import { Head, usePage } from '@inertiajs/react'
import { appsignal } from 'lib/appsignal'
import { AlertManager, AlertProvider } from 'shared/AlertManager'
import { Fallback } from 'shared/Fallback'
import { I18nProvider } from 'shared/I18n'
import { theme } from 'theme'

import { LicenseInfo, muiXTelemetrySettings } from '@mui/x-license'

import { DialogsProvider, LocaleProvider, ThemeProvider } from '@oxfordmedicalsimulation/oms-fe-components'

// No overarching React App to add the license key to, so we need to set it here to ensure the MUI X Pro components work correctly on every page that needs it.
LicenseInfo.setLicenseKey(import.meta.env.VITE_APP_MUI_XPRO_KEY || '')
muiXTelemetrySettings.disableTelemetry()

export const BaseLayout = ({ children }: { children: React.ReactNode }) => {
  const page = usePage()

  const tags = useMemo(
    () => ({
      inertia_component: page.component,
    }),
    [page.component]
  )

  const fallback = useCallback((error: unknown) => {
    if (import.meta.env.DEV) {
      return (
        <div style={{ padding: 16 }}>
          <h2>Oh No! The Hub frontend just crashed</h2>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{String(error)}</pre>
        </div>
      )
    }

    return <Fallback />
  }, [])

  const content = (
    <ThemeProvider theme={theme}>
      <LocaleProvider>
        <AlertProvider>
          <AlertManager />
          <DialogsProvider>{children}</DialogsProvider>
        </AlertProvider>
      </LocaleProvider>
    </ThemeProvider>
  )

  return (
    <I18nProvider>
      <Head>
        <title>Oxford Medical Simulation - VR training for optimal patient care</title>
        <meta name="description" content="VR training for optimal patient care" />
        <link rel="icon" type="image/svg+xml" href="/favicon.ico" />
      </Head>
      {appsignal ? (
        <ErrorBoundary instance={appsignal} action="App" tags={tags} fallback={fallback}>
          {content}
        </ErrorBoundary>
      ) : (
        content
      )}
    </I18nProvider>
  )
}
